import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ProjectSelector.scss";
import { connect } from "react-redux";
import { IApplicationState } from "src/reducer";
import Label from "../Label/Label";
import { ContextualMenu, IContextualMenuItemProps, TooltipHost } from "@fluentui/react";
import UnstyledButton from "../UnstyledButton/UnstyledButton";
import Visage2Icon from "../Visage2Icon/Visage2Icon";
import { localize } from "src/utils/l10n";
import moment from "moment";
import { setActiveProjectId } from "src/actions/project";
import { getProjectStatusColor } from "src/utils";
import api from "src/api/SpintrApi";
import { AxiosResponse } from "axios";
import { useHistory } from "react-router-dom";

interface IProps {
    projects?: any[];
    activeProjectId?: number;
    dispatch?: any;
}

interface ProjectSelectorProjectProps {
    project: any;
}

const ProjectSelectorProject = (props: ProjectSelectorProjectProps) => {
    return (
        <div className="ProjectSelectorProject">
            <div className="project-selector-top">
                <Label size="h5" weight="semi-bold" fontType="display">{props.project.name}</Label>
            </div>
            <div className="project-selector-meta">
                <span>
                    <Label color="p-mid-grey-1" size="body-3">{localize("CREATED")}</Label>
                    <Label color="p-mid-grey-5" size="body-3">{moment(props.project.createdDate).format("ll")}</Label>
                </span>
                <Label color={getProjectStatusColor(props.project.status)} size="body-3">{localize("PROJECT_STATUS_" + props.project.status)}</Label>
            </div>
        </div>
    )
}

const ProjectSelector = (props: IProps) => {
    const linkRef = React.useRef(null);
    const [showMenu, setShowMenu] = useState(false);
    const [eNPS, setENPS] = useState<any>(undefined);
    const history = useHistory();

    const activeProject = useMemo(() => {
        return props.projects.find(x => x.id === props.activeProjectId);
    }, [props.activeProjectId, props.projects]);

    useEffect(() => {
        if (activeProject.hasENPSQuestion) {
            api.get("/api/v1/surveys/" + props.activeProjectId + "/enps").then((response: AxiosResponse) => {
                setENPS(response.data);
            }).catch(() => {
                setENPS(undefined);
            });
        }
    }, [activeProject]);

    const eNPSText = useMemo(() => {
        if (!eNPS) {
            return localize("NO_ENPS_DATA_DESCRIPTION");
        }

        return localize("SURVEY") + ": " + moment(eNPS.date).format("ll") + "<br/>" +
            localize("SCORE") + ": " + eNPS.score + "<br/>" +
            "Promoters: " + eNPS.promoterCount + "<br/>" +
            "Passives: " + eNPS.passiveCount + "<br/>" +
            "Detractors: " + eNPS.detractorCount + "<br/>";
    }, [eNPS]);

    if (!activeProject) {
        return null;
    }

    return (
        <div className="ProjectSelector">
            <UnstyledButton onClick={() => {
                setShowMenu(true);
            }}>
                <div className="project-selector-top" ref={linkRef}>
                    <Label size="h3" weight="semi-bold" fontType="display">{activeProject.name}</Label>
                    <Visage2Icon icon="arrow-down-1" size="small" />
                </div>
                <div className="project-selector-meta">
                    <span>
                        <Label color="p-mid-grey-5" size="body-3">{localize("CREATED") + ": "}</Label>
                        <Label color="p-mid-grey-5" size="body-3">{moment(activeProject.createdDate).format("ll")}</Label>
                    </span>
                    <span>
                        <Label color="p-mid-grey-5" size="body-3">{localize("STATUS") + ": "}</Label>
                        <Label color={getProjectStatusColor(activeProject.status)} size="body-3">{localize("PROJECT_STATUS_" + activeProject.status)}</Label>
                    </span>
                    {activeProject.hasENPSQuestion && (
                        <TooltipHost
                            className="marketplace-tooltip"
                            content={
                                <span dangerouslySetInnerHTML={{
                                    __html: eNPSText
                                }} />
                            }
                        >
                            <span>
                                <Label color="p-mid-grey-5" size="body-3">{"eNPS: "}</Label>
                                <Label color="p-mid-grey-5" size="body-3">{eNPS?.score || localize("NO_DATA")}</Label>
                            </span>
                        </TooltipHost>
                    )}
                </div>
            </UnstyledButton>
            <ContextualMenu
                className="ProjectSelectorMenu"
                contextualMenuItemAs={(contextualMenuItemProps: IContextualMenuItemProps) => {
                    return (
                        <ProjectSelectorProject project={props.projects.find(x => x.id === contextualMenuItemProps.item.key)} />
                    )
                }}
                items={props.projects.map((x: any) => {
                    return {
                        key: x.id,
                        text: x.name,
                        onClick: () => {
                            props.dispatch(setActiveProjectId(x.id));
                            history.push("/");
                        }
                    }
                })}
                hidden={!showMenu}
                target={linkRef}
                onItemClick={() => {
                    setShowMenu(false);
                }}
                onDismiss={() => {
                    setShowMenu(false);
                }}
            />
        </div>
    )
}

const mapStateToProps = (state: IApplicationState, props) => ({
    ...props,
    projects: state.project.projects,
    activeProjectId: state.project.activeProjectId
});

export default connect(mapStateToProps)(ProjectSelector);

